import { useMemo, useState, useEffect } from 'react';
import { downloadAsExcel } from '../../../../utils/downloadAsExcel';
import styled, { css } from 'styled-components';
import { s } from '../PeriodStatsPage.styles';
import InsightSummary from '../InsigntSummary';
import { formatCurrency } from '../../../../func/Function';
import { useDragScroll } from '../../../../utils/useDragScroll';
import BottomModal from '../../../components/modal/BottomModal';

export type RankItem = {
  id: string;
  caseNo: string;
  address: string;
  usage: string;
  claimAmount: number; // 청구금액 (claim_price)
  winningBidAmount: number; // 낙찰가 (bid_price)
  winningPriceRate: number; // 낙찰가율 (bid_price_rate)
  collateralValueRate: number; // 담보가치율 (collateral_value_rate)
  recoveryRate: number; // 회수율 (recovery_rate)
  mortgageAmount?: number; // 근저당액 (mortgage_amount)
  lossRate?: number; // 손실률 (loss_rate)
  shortfallRate?: number; // 회수부족율 (recall_shortage_rate)
};

type AuctionRankProps = {
  periodSummary: string;
  region: string;
  usage: string;
  usageDetail?: string;
  court?: string;
  fiMain?: string;
  fiSub?: string;
  type: 'week' | 'month' | 'year';
  selectedType: 'A' | 'B' | 'C' | 'D' | 'E';
  onTypeChange: (t: 'A' | 'B' | 'C' | 'D' | 'E') => void;
  data: RankItem[];
  loading?: boolean;
  insights?: string;
  insightsLoading?: boolean;
  insightsError?: string | null;
};

type TabKey = 'A' | 'B' | 'C' | 'D' | 'E';

const TAB_LABEL: Record<TabKey, string> = {
  A: '청구금액',
  B: '낙찰가',
  C: '낙찰가율',
  D: '담보가치율',
  E: '회수율',
};

type SortKey =
  | 'rank'
  | 'claimAmount'
  | 'winningBidAmount'
  | 'winningPriceRate'
  | 'collateralValueRate'
  | 'recoveryRate';
type SortDir = 'asc' | 'desc';

const DEFAULT_SORT_BY_TAB: Record<TabKey, { key: SortKey; dir: SortDir }> = {
  A: { key: 'claimAmount', dir: 'desc' }, // 청구금액
  B: { key: 'winningBidAmount', dir: 'desc' }, // 낙찰가
  C: { key: 'winningPriceRate', dir: 'desc' }, // 낙찰가율
  D: { key: 'collateralValueRate', dir: 'desc' }, // 담보가치율
  E: { key: 'recoveryRate', dir: 'desc' }, // 회수율
};

export default function NPLRank({
  periodSummary,
  region,
  usage,
  usageDetail,
  court,
  fiMain,
  fiSub,
  type,
  selectedType,
  onTypeChange,
  data,
  loading = false,
  insights,
  insightsLoading,
  insightsError,
}: AuctionRankProps) {
  const [tab, setTab] = useState<TabKey>(selectedType);
  useEffect(() => {
    setTab(selectedType);
    setSortKey(DEFAULT_SORT_BY_TAB[selectedType].key);
    setSortDir(DEFAULT_SORT_BY_TAB[selectedType].dir);
    setShowAll(false);
  }, [selectedType]);

  const [sortKey, setSortKey] = useState<SortKey>(
    DEFAULT_SORT_BY_TAB[selectedType].key,
  );
  const [sortDir, setSortDir] = useState<SortDir>(
    DEFAULT_SORT_BY_TAB[selectedType].dir,
  );
  const [showAll, setShowAll] = useState(false);

  const onChangeTab = (t: TabKey) => {
    setTab(t);
    setSortKey(DEFAULT_SORT_BY_TAB[t].key);
    setSortDir(DEFAULT_SORT_BY_TAB[t].dir);
    setShowAll(false);
    onTypeChange(t);
  };

  const { ref, onMouseDown, onMouseLeave, onMouseUp, onMouseMove } =
    useDragScroll();

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };
  const makeStableId = (r: RankItem) => {
    const num = (v: unknown) => (typeof v === 'number' && !isNaN(v) ? v : -1);
    const norm = (s: unknown) => String(s ?? '').trim();

    return [
      norm(r.id),
      norm(r.caseNo),
      norm(r.address),
      num(r.claimAmount),
      num(r.winningBidAmount),
      num(r.winningPriceRate),
      num(r.collateralValueRate),
      num(r.recoveryRate),
      num(r.mortgageAmount),
      num(r.lossRate),
      num(r.shortfallRate),
    ].join('::');
  };

  const toNum = (v: unknown, fallback = 0) => {
    if (v == null) return fallback;
    if (typeof v === 'number' && !isNaN(v)) return v;
    // "12,345" 같은 경우도 대비
    const n = Number(String(v).replace(/,/g, ''));
    return Number.isFinite(n) ? n : fallback;
  };

  const prepared = useMemo(() => {
    const base = Array.isArray(data) ? data : [];

    return base.map((raw: any) => {
      // raw가 이미 RankItem 형태면 그대로, 아니면 API 키에서 가져오기
      const r: RankItem = {
        id: String(raw.id ?? ''), // 어차피 아래에서 makeStableId로 덮어씀
        caseNo: String(raw.caseNo ?? raw.casenumber ?? '').trim(),
        address: String(raw.address ?? raw.location ?? '').trim(),
        usage: String(raw.usage ?? raw.use ?? '').trim(),
        claimAmount: toNum(raw.claimAmount ?? raw.claim_price),
        winningBidAmount: toNum(raw.winningBidAmount ?? raw.bid_price),
        winningPriceRate: toNum(raw.winningPriceRate ?? raw.bid_price_rate),
        collateralValueRate: toNum(
          raw.collateralValueRate ?? raw.collateral_value_rate,
        ),
        recoveryRate: toNum(raw.recoveryRate ?? raw.recovery_rate),
        mortgageAmount: toNum(raw.mortgageAmount ?? raw.mortgage_amount),
        lossRate: toNum(raw.lossRate ?? raw.loss_rate),
        shortfallRate: toNum(raw.shortfallRate ?? raw.recall_shortage_rate),
      };

      return {
        ...r,
        // 안정적인 키로 다시 생성
        id: makeStableId(r),
      };
    });
  }, [data]);

  const sorted = useMemo(() => {
    const base = [...prepared];

    const pick = (row: RankItem & { rank?: number }, key: SortKey) => {
      switch (key) {
        case 'rank':
          return row.rank ?? 0;
        case 'claimAmount':
          return row.claimAmount;
        case 'winningBidAmount':
          return row.winningBidAmount;
        case 'winningPriceRate':
          return row.winningPriceRate;
        case 'collateralValueRate':
          return row.collateralValueRate;
        case 'recoveryRate':
          return row.recoveryRate;
        default:
          return 0;
      }
    };

    const primary = DEFAULT_SORT_BY_TAB[tab];
    base.sort((a, b) => {
      const diff = pick(a, primary.key) - pick(b, primary.key);
      if (diff !== 0) return primary.dir === 'asc' ? diff : -diff;
      return (
        a.caseNo.localeCompare(b.caseNo) || a.address.localeCompare(b.address)
      );
    });

    const withRank = base.map((row, idx) => ({ ...row, rank: idx + 1 }));

    const final = [...withRank].sort((a, b) => {
      const diff = pick(a, sortKey) - pick(b, sortKey);
      if (diff !== 0) return sortDir === 'asc' ? diff : -diff;
      return (
        a.caseNo.localeCompare(b.caseNo) || a.address.localeCompare(b.address)
      );
    });

    return final;
  }, [prepared, tab, sortKey, sortDir]);

  // 엑셀로 내릴 라벨(탭명)
  const tabTitle = TAB_LABEL;

  const handleDownloadExcel = () => {
    // 화면에 보이는 순서/개수와 동일하게 (Top10 또는 전체)
    const visible = sorted.slice(0, showAll ? sorted.length : 10);

    // 엑셀 행 구성 (가독성 위해 포맷팅된 문자열로 저장)
    const rows = visible.map((r) => ({
      순위: r.rank,
      사건번호: r.caseNo,
      소재지: r.address,
      용도: r.usage,
      청구금액: r.claimAmount.toLocaleString('ko-KR'),
      낙찰가: r.winningBidAmount.toLocaleString('ko-KR'),
      '낙찰가율(%)': r.winningPriceRate.toFixed(1),
      '담보가치율(%)': r.collateralValueRate.toFixed(1),
      '회수율(%)': r.recoveryRate.toFixed(1),
    }));

    const filename = `주요_경매_물건_${tabTitle[tab]}`;
    downloadAsExcel(rows as any[], filename);
  };

  const insightContent = useMemo(() => {
    if (insightsLoading) return '인사이트를 불러오는 중…';
    if (insightsError) return insightsError;
    const t = (insights ?? '').trim();
    return t.length ? t : '표시할 인사이트가 없어요.';
  }, [insights, insightsLoading, insightsError]);

  const [selectedRow, setSelectedRow] = useState<RankItem | null>(null);

  return (
    <s.Wrap>
      <s.Header>
        <s.HeaderRow>
          <s.Img>
            <img src="/assets/images/icon/trophy.svg" alt="" />
          </s.Img>
          <s.ExcelButton onClick={handleDownloadExcel} aria-label="엑셀 저장">
            <img src="/assets/images/icon/download-black.svg" alt="" />
          </s.ExcelButton>
        </s.HeaderRow>
        <s.TitleBox>
          <h3>주요 경매 대상 NPL 물건 순위</h3>
          <p>청구액·낙찰가·낙찰가율·담보가치율·회수율 기준 상위 NPL 물건</p>
          <s.SelectedOptionBox>
            <s.SelectedOption>{periodSummary}</s.SelectedOption>
            <s.SelectedOption>
              {court && court !== 'all' ? court : `${region} 지역`}
            </s.SelectedOption>
            <s.SelectedOption>
              {usage === '전체'
                ? `${usage} 용도`
                : `${usage} ${usageDetail ? ` ${usageDetail} 용도` : ''}`}
            </s.SelectedOption>
            <s.SelectedOption>
              {fiMain === '전체'
                ? `${fiMain} 금융기관`
                : `${fiSub ? ` ${fiSub}` : ''}`}
            </s.SelectedOption>
          </s.SelectedOptionBox>
        </s.TitleBox>
      </s.Header>

      <TabRow>
        {(Object.keys(TAB_LABEL) as TabKey[]).map((k) => (
          <TabPill key={k} $active={tab === k} onClick={() => onChangeTab(k)}>
            {TAB_LABEL[k]}
          </TabPill>
        ))}
      </TabRow>

      {loading ? (
        <LoaderWrap>
          <LoaderImg
            src="/assets/images/icon/loader.gif"
            alt="목록을 불러오는 중"
          />
        </LoaderWrap>
      ) : (
        <>
          <InsightSummary
            title="인사이트 요약"
            category={TAB_LABEL[tab]}
            content={insightContent}
          />

          <TableContainer
            ref={ref}
            onMouseDown={onMouseDown}
            onMouseLeave={onMouseLeave}
            onMouseUp={onMouseUp}
            onMouseMove={onMouseMove}
          >
            <s.Table>
              <thead>
                <tr>
                  <Th width="76px" align="left">
                    <SortHeader
                      $align="left"
                      onClick={() => toggleSort('rank')}
                    >
                      <Arrow $active={sortKey === 'rank'} $dir={sortDir} /> 순위
                    </SortHeader>
                  </Th>
                  <Th width="242px">용도/사건번호 / 소재지</Th>
                  <Th width="120px">
                    <SortHeader
                      $align="right"
                      onClick={() => toggleSort('claimAmount')}
                    >
                      <Arrow
                        $active={sortKey === 'claimAmount'}
                        $dir={sortDir}
                      />{' '}
                      청구금액
                    </SortHeader>
                  </Th>
                  <Th width="120px">
                    <SortHeader
                      $align="right"
                      onClick={() => toggleSort('winningBidAmount')}
                    >
                      <Arrow
                        $active={sortKey === 'winningBidAmount'}
                        $dir={sortDir}
                      />{' '}
                      낙찰가
                    </SortHeader>
                  </Th>
                  <Th width="120px">
                    <SortHeader
                      $align="right"
                      onClick={() => toggleSort('winningPriceRate')}
                    >
                      <Arrow
                        $active={sortKey === 'winningPriceRate'}
                        $dir={sortDir}
                      />{' '}
                      낙찰가율
                    </SortHeader>
                  </Th>
                  <Th width="108px" align="right">
                    <SortHeader
                      $align="right"
                      onClick={() => toggleSort('collateralValueRate')}
                    >
                      <Arrow
                        $active={sortKey === 'collateralValueRate'}
                        $dir={sortDir}
                      />{' '}
                      담보가치율
                    </SortHeader>
                  </Th>
                  <Th width="108px" align="center">
                    <SortHeader
                      onClick={() => toggleSort('recoveryRate')}
                      $align="right"
                    >
                      <Arrow
                        $active={sortKey === 'recoveryRate'}
                        $dir={sortDir}
                      />{' '}
                      회수율
                    </SortHeader>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {sorted.slice(0, showAll ? sorted.length : 10).map((row) => (
                  <Row
                    key={row.id}
                    $selected={row.id === selectedRow?.id}
                    onClick={() => setSelectedRow(row)}
                  >
                    <Td align="center">{row.rank}</Td>
                    <Td>
                      <CaseBox>
                        <span>
                          <Category>{row.usage}</Category> {row.caseNo}
                        </span>
                        <Addr title={row.address}>{row.address}</Addr>
                      </CaseBox>
                    </Td>
                    <Td align="right">{formatCurrency(row.claimAmount)}</Td>
                    <Td align="right">
                      {formatCurrency(row.winningBidAmount)}
                    </Td>
                    <Td align="right">{row.winningPriceRate}%</Td>
                    <Td align="right">{row.collateralValueRate}%</Td>
                    <Td align="right">{row.recoveryRate}%</Td>
                  </Row>
                ))}
              </tbody>
            </s.Table>
          </TableContainer>
          {sorted.length > 10 && (
            <s.MoreButton onClick={() => setShowAll((v) => !v)}>
              <span>{showAll ? '접기' : '더보기'}</span>
              <img
                src="/assets/images/arrow/arrow-down-gray.svg"
                alt=""
                style={{
                  transform: showAll ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </s.MoreButton>
          )}
        </>
      )}
      {selectedRow && (
        <BottomModal onClose={() => setSelectedRow(null)}>
          <TooltipHeader>
            <HeaderLeft>
              <BadgeLabel>{selectedRow.usage}</BadgeLabel>
              <TooltipTitle>{selectedRow.caseNo}</TooltipTitle>
            </HeaderLeft>
            <CloseBtn
              onClick={() => setSelectedRow(null)}
              aria-label="툴팁 닫기"
            >
              ×
            </CloseBtn>
          </TooltipHeader>

          <TooltipList>
            <TipItem>
              <span>낙찰가</span>
              <strong>{formatCurrency(selectedRow.winningBidAmount)}</strong>
            </TipItem>
            <TipItem>
              <span>낙찰가율</span>
              <strong>{`${selectedRow.winningPriceRate.toFixed?.(1) ?? selectedRow.winningPriceRate}%`}</strong>
            </TipItem>
            <TipItem>
              <span>근저당액</span>
              <strong>
                {selectedRow.mortgageAmount != null
                  ? formatCurrency(selectedRow.mortgageAmount)
                  : '-'}
              </strong>
            </TipItem>
            <TipItem>
              <span>청구금액</span>
              <strong>{formatCurrency(selectedRow.claimAmount)}</strong>
            </TipItem>
            <TipItem>
              <span>담보가치율</span>
              <strong>{`${selectedRow.collateralValueRate.toFixed?.(1) ?? selectedRow.collateralValueRate}%`}</strong>
            </TipItem>
            <TipItem>
              <span>회수율</span>
              <strong>{`${selectedRow.recoveryRate.toFixed?.(1) ?? selectedRow.recoveryRate}%`}</strong>
            </TipItem>
            <TipItem>
              <span>손실률</span>
              <strong>{selectedRow.lossRate}</strong>
            </TipItem>
            <TipItem>
              <span>회수부족율</span>
              <strong>{selectedRow.shortfallRate}</strong>
            </TipItem>
          </TooltipList>
          <ToolTipBottom>
            <ToolTipButton onClick={() => setSelectedRow(null)}>
              확인
            </ToolTipButton>
          </ToolTipBottom>
        </BottomModal>
      )}
    </s.Wrap>
  );
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 40px;
`;

const TabRow = styled.div`
  display: flex;
  flex-direction: row;
  background: #f4f5f7;
  border-radius: 8px;
  padding: 2px;
  overflow-x: auto;
  width: 100%;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const TabPill = styled.button<{
  $active?: boolean;
}>`
  flex: 1 0 0;
  padding: 8px 12px;
  background: transparent;
  border: none;
  color: #a7adb8;
  font-size: 14px;
  cursor: pointer;
  border-radius: 6px;
  ${({ $active }) =>
    $active &&
    css`
      background: #fff;
      color: #0c0e12;
      font-weight: 500;
      box-shadow:
        0px 4px 16px 0px rgba(39, 44, 54, 0.08),
        0px 1px 2px 0px rgba(39, 44, 54, 0.12),
        0px 2px 6px 0px rgba(39, 44, 54, 0.05);
    `}

  outline: none;
  border: none;

  &:focus {
    outline: none;
  }

  &:active {
    outline: none;
  }
`;

const Th = styled.th<{ width?: string; align?: 'left' | 'center' | 'right' }>`
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  letter-spacing: -2%;
  angle: 0 deg;
  opacity: 1;
  gap: 4px;
  padding: 12px 14px 12px 12px;
  color: #424854;
  text-align: ${(p) => p.align ?? 'left'};
  width: ${(p) => p.width ?? 'auto'};
`;

const Td = styled.td<{ align?: 'left' | 'center' | 'right' }>`
  padding: 14px 12px;
  font-size: 14px;
  font-weight: 500;
  color: #424854;
  text-align: ${(p) => p.align ?? 'left'};
  vertical-align: top;
`;

const SortHeader = styled.button<{ $align?: 'left' | 'center' | 'right' }>`
  font-weight: 500;
  font-size: 13px;
  leading-trim: NONE;
  line-height: 20px;
  letter-spacing: -2%;
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  cursor: pointer;
  background: transparent;
  justify-content: ${({ $align }) =>
    $align === 'right'
      ? 'flex-end'
      : $align === 'center'
        ? 'center'
        : 'flex-start'};

  outline: none;
  border: none;

  &:focus {
    outline: none;
  }

  &:active {
    outline: none;
  }
`;

const Arrow = styled.img.attrs<{ $active?: boolean; $dir?: 'asc' | 'desc' }>(
  ({ $active, $dir }) => {
    if ($active) {
      return {
        src:
          $dir === 'desc'
            ? '/assets/images/arrow/blue_arrow_down.svg' // 활성 + 내림
            : '/assets/images/arrow/red_arrow_up.svg', // 활성 + 오름
        alt: '',
      };
    }
    return { src: '/assets/images/arrow/gray_arrow_down.svg', alt: '' };
  },
)`
  width: 16px;
  height: 16px;
  transition:
    transform 120ms ease,
    opacity 120ms ease;
`;

const CaseBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Addr = styled.span`
  color: #787d87;
  font-weight: 500;
  font-size: 13px;
`;

const Category = styled.span`
  background-color: #e8e9ed;
  color: #424854;
  font-weight: 700;
  font-size: 13px;
  border-radius: 4px;
  padding: 0px 8px;
  text-align: center;
  width: fit-content;
`;

const LoaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  width: 100%;
`;

const LoaderImg = styled.img`
  width: 24px;
  height: 24px;
`;

// 행 hover/selected
const Row = styled.tr<{ $selected?: boolean }>`
  cursor: pointer;
  background: ${({ $selected }) => ($selected ? '#E8E9ED' : 'transparent')};
  &:hover {
    background: ${({ $selected }) => ($selected ? '#E8E9ED' : '#F4F5F7')};
  }
  transition: background 120ms ease;
`;

const TooltipHeader = styled.div`
  display: flex;
  padding: 24px 20px 8px 20px;
  align-items: center;
  align-self: stretch;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
`;

const BadgeLabel = styled.span`
  background: #e8e9ed;
  color: #424854;
  font-weight: 700;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 6px;
`;

const TooltipTitle = styled.div`
  color: #424854;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 150% */
  letter-spacing: -0.32px;
`;

const CloseBtn = styled.button`
  background: transparent;
  border: none;
  outline: none;

  font-size: 16px;
  color: #787d87;
  cursor: pointer;
  line-height: 1;
`;

const TooltipList = styled.div`
  display: flex;
  padding-top: 8px;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const TipItem = styled.div`
  display: flex;
  padding: 4px 24px 12px 24px;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  align-self: stretch;

  span {
    font-size: 13px;
    color: #787d87;
  }
  strong {
    font-size: 14px;
    color: #424854;
  }
`;

const ToolTipBottom = styled.div`
  display: flex;
  padding: 16px 24px 24px 24px;
  align-items: center;
  gap: 8px;
`;

const ToolTipButton = styled.div`
  display: flex;
  height: 44px;
  padding: 0 20px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;
  border-radius: 8px;
  background: var(--npl-semantic-background-solid-light-01, #f4f5f7);
  color: var(--npl-semantic-label-solid-light-secondary, #424854);

  /* npl/body/med/L_15 */
  font-family: 'Spoqa Han Sans Neo';
  font-size: 15px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px; /* 146.667% */
  letter-spacing: -0.3px;
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  cursor: grab;

  &.grabbing {
    cursor: grabbing;
  }

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
  }
`;
